import type { BlockNoteEditor } from "@blocknote/core";
import { getMarkThreadId } from "./editorCommentThread";

const collectTextSpans = (editor: BlockNoteEditor) => {
  const spans: Array<{ from: number; text: string }> = [];
  editor.prosemirrorState.doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return true;
    spans.push({ from: pos, text: node.text });
    return true;
  });
  return spans;
};

const rangeFromTextIndex = (
  spans: Array<{ from: number; text: string }>,
  index: number,
  length: number
): { from: number; to: number } | null => {
  let scanned = 0;
  let from: number | null = null;
  let remaining = length;

  for (const span of spans) {
    const next = scanned + span.text.length;
    if (from === null) {
      if (index >= next) {
        scanned = next;
        continue;
      }
      const offset = index - scanned;
      from = span.from + offset;
      remaining -= span.text.length - offset;
      if (remaining <= 0) return { from, to: from + length };
      scanned = next;
      continue;
    }

    remaining -= span.text.length;
    if (remaining <= 0) {
      return { from, to: span.from + span.text.length + remaining };
    }
    scanned = next;
  }

  return from === null ? null : { from, to: from + length };
};

const threadOwnerInRange = (
  editor: BlockNoteEditor,
  from: number,
  to: number
) => {
  let owner: string | null = null;
  editor.prosemirrorState.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return true;
    node.marks.forEach((mark) => {
      const threadId = getMarkThreadId(mark);
      if (threadId && !owner) owner = threadId;
    });
    return true;
  });
  return owner;
};

/** First quote occurrence not owned by a different thread. */
export const findQuoteRange = (
  editor: BlockNoteEditor,
  quote: string,
  threadId?: string
): { from: number; to: number } | null => {
  const needle = quote.trim();
  if (!needle) return null;

  const spans = collectTextSpans(editor);
  const fullText = spans.map((span) => span.text).join("");
  let searchFrom = 0;
  let unmarked: { from: number; to: number } | null = null;

  while (searchFrom <= fullText.length - needle.length) {
    const index = fullText.indexOf(needle, searchFrom);
    if (index === -1) break;
    const range = rangeFromTextIndex(spans, index, needle.length);
    searchFrom = index + 1;
    if (!range) continue;

    const owner = threadOwnerInRange(editor, range.from, range.to);
    if (threadId && owner === threadId) return range;
    if (owner && owner !== threadId) continue;
    if (!unmarked) unmarked = range;
  }

  return unmarked;
};
